import React from 'react';
import {
  Box,
  HeadingText,
  Card,
  ParagraphText,
  TextAction,
  Tabs,
} from '@leagueplatform/genesis-core';
import { useNavigateLegacy as useNavigate } from '@leagueplatform/routing';
import { useStaticAsset } from '@leagueplatform/asset-config';
import { EVENT_NAME, trackAnalyticsEvent } from '@leagueplatform/analytics';
import { DOCUMENT_CENTER_ASSET_KEYS } from '../types/document-center.types';
import * as CONSTANTS from '../utils/constant';
import { NoDocsComponent } from './no-docs.component';
import { ListingComponent } from './listing.component';
import { getDocumentsList } from '../helper/helpers';

export function TabsComponent(props: any) {
  const {
    docsList,
    ubkValue,
    tabActive,
    setTabActive,
    isLoading,
    setIsLoading,
    setErrMsg,
    accessToken,
    medicareMember,
    fireBaseFlagsConfig,
  } = props;

  const navigate = useNavigate();

  const rightIndicator = useStaticAsset(
    DOCUMENT_CENTER_ASSET_KEYS.RIGHT_INDICATOR,
  );
  const documentTypes = getDocumentsList(
    ubkValue,
    medicareMember,
    fireBaseFlagsConfig,
  );

  const sortedDocumentsList = [...Object.values(documentTypes)].sort((a, b) =>
    a.name.localeCompare(b.name),
  );

  return (
    <Tabs.Root defaultValue={tabActive}>
      <Tabs.TabList
        css={{
          boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.06)',
        }}
      >
        <Tabs.Tab
          value={CONSTANTS.RECENT}
          css={{
            padding: '12px 8px',
            fontWeight: 400,
            '&:focus': { outline: 'none !important' },
            '@mobile': {
              padding: '8px',
            },
          }}
          onClick={() => {
            setTabActive(CONSTANTS.RECENT);
            trackAnalyticsEvent(EVENT_NAME.NAVIGATION_TAB_CLICKED, {
              detail: CONSTANTS.RECENT_DOCUMENTS,
              screen_name: CONSTANTS.DOCUMENT_CENTER,
              product_area: CONSTANTS.DOCUMENT_CENTER,
              path: window.location.pathname,
              event_author: CONSTANTS.ENGEN,
            });
          }}
        >
          {CONSTANTS.RECENT_DOCUMENTS}
        </Tabs.Tab>
        <Tabs.Tab
          value={CONSTANTS.ALL}
          css={{
            padding: '12px 8px',
            fontWeight: 400,
            '&:focus': { outline: 'none !important' },
          }}
          onClick={() => {
            setTabActive(CONSTANTS.ALL);
            trackAnalyticsEvent(EVENT_NAME.NAVIGATION_TAB_CLICKED, {
              detail: CONSTANTS.ALL_DOCUMENTS,
              screen_name: CONSTANTS.DOCUMENT_CENTER,
              product_area: CONSTANTS.DOCUMENT_CENTER,
              path: window.location.pathname,
              event_author: CONSTANTS.ENGEN,
            });
          }}
        >
          {CONSTANTS.ALL_DOCUMENTS}
        </Tabs.Tab>
      </Tabs.TabList>
      <Tabs.TabPanel
        value="recent"
        css={{
          padding: '0 24px',
          '@mobile': {
            '& .card-action': {
              marginTop: '32px',
            },
            '& .card-elevated': {
              marginTop: 0,
            },
          },
          '@laptop': {
            padding: '0px',
          },
          '@desktop': {
            padding: '0px',
          },
        }}
      >
        {docsList.length ? (
          <ListingComponent
            docsList={docsList}
            isAllDocs={false}
            setIsLoading={setIsLoading}
            setErrMsg={setErrMsg}
            accessToken={accessToken}
          />
        ) : null}
        {!docsList.length && !isLoading ? (
          <Box
            css={{
              marginTop: '70px',
            }}
          >
            <NoDocsComponent
              isAllDocs={false}
              letterType={CONSTANTS.RECENT_DOCUMENTS}
            />
          </Box>
        ) : null}
      </Tabs.TabPanel>
      <Tabs.TabPanel
        value="All"
        css={{
          padding: '0 24px',
          '@laptop': {
            padding: '0px',
          },
          '@desktop': {
            padding: '0px',
          },
        }}
      >
        {sortedDocumentsList.length &&
          sortedDocumentsList.map((item: any) => (
            <Card.Elevated
              css={{
                border: '1px solid #E0E3E6',
                alignItems: 'center',
                padding: '24px',
                background: '#fff',
                marginTop: '32px',
                backgroundColor: '#fff',
                boxShadow: '0px 2px 6px 0px rgba(0, 0, 0, 0.08)',
                borderRadius: '9px',
              }}
              imagePosition={{ '@initial': 'end' }}
              key={item.id}
            >
              <Card.Section
                padding={{
                  '@initial': '$none',
                  '@mobile': '$none',
                  '@tablet': '$none',
                  '@desktop': '$none',
                }}
              >
                <Box
                  css={{
                    display: 'flex',
                    margin: '12px 0 16px',
                    '@desktop': { margin: '12px 0 24px' },
                    '@laptop': { margin: '12px 0 24px' },
                  }}
                >
                  <HeadingText
                    size="sm"
                    level="4"
                    css={{
                      fontSize: '20px',
                      lineHeight: '28px',
                      fontFamily: `${CONSTANTS.FONT_POPPINS}`,
                      fontWeight: 600,
                      color: '#2C3135',
                    }}
                  >
                    {item.name}
                  </HeadingText>
                </Box>
                <ParagraphText
                  css={{
                    fontSize: '16px',
                    margin: '0 0',
                    fontFamily: `${CONSTANTS.FONT_ROBOTO}`,
                    color: '#2C3135',
                    lineHeight: '24px',
                    fontWeight: 400,
                    '@desktop': { maxWidth: '80%' },
                    '@laptop': { maxWidth: '80%' },
                    marginBlockEnd: '0px !important',
                  }}
                >
                  {item.description}
                </ParagraphText>
                <TextAction
                  css={{
                    color: '#0074C9',
                    fontSize: '16px',
                    lineHeight: '24px',
                    letterSpacing: '-0.2px',
                    verticalAlign: 'top',
                    marginTop: '24px',
                  }}
                  onClick={() => {
                    navigate(`${CONSTANTS.DOCUMENTS_URL}/${item.id}`);
                    trackAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, {
                      detail: item.label,
                      product_area: CONSTANTS.DOCUMENT_CENTER,
                      sub_product_area: CONSTANTS.ALL_DOCUMENTS,
                      screen_name: CONSTANTS.DOCUMENT_CENTER,
                      path: window.location.pathname,
                      event_author: CONSTANTS.ENGEN,
                    });
                  }}
                >
                  {item.label}
                  <img
                    src={rightIndicator as string}
                    alt="icon"
                    style={{ margin: '1px 0 0 6px' }}
                  />
                </TextAction>
              </Card.Section>
            </Card.Elevated>
          ))}
      </Tabs.TabPanel>
    </Tabs.Root>
  );
}
